import { EndpointPermissionRule } from '../core/permissions/endpoint-permission.models';
import {
  HESAPLAMA_TOPLU_VERI_PERMISSIONS,
  P,
} from './portal-permissions.constants';

/** Yeni TopluVeri + legacy VeriYukleme (roller kırılmasın) */
const TOPLU = [...HESAPLAMA_TOPLU_VERI_PERMISSIONS];

/** Admin ekipman import tab'ı da aynı job API'lerini kullanır */
const TOPLU_OR_EKIPMAN = [...TOPLU, P.Yonetim.Ekipmanlar];

function multiRead(
  pattern: string,
  permissionKeys: string[],
  methods: ('GET' | 'POST')[] = ['GET', 'POST'],
): EndpointPermissionRule[] {
  return methods.map((method) => ({
    method,
    pattern,
    permissionKeys,
    access: 'read' as const,
  }));
}

function multiWrite(
  pattern: string,
  permissionKeys: string[],
  methods: ('GET' | 'POST')[] = ['POST'],
): EndpointPermissionRule[] {
  return methods.map((method) => ({
    method,
    pattern,
    permissionKeys,
    access: 'write' as const,
  }));
}

/**
 * Hesaplama — Toplu Veri Yükleme / import job / hata editörü.
 *
 * Read  : Summaries, SummaryDetail, SummaryDetailExcel, Get*EditableRows
 * Write : Upload*, EmissionDataBulkProcess, Delete*ImportJob,
 *         Update*ImportRows / Delete*ImportRows / Update|Delete EmissionBulkProcessRows
 */
export const HESAPLAMA_ENDPOINT_EXTRA_RULES: readonly EndpointPermissionRule[] =
  [
    /* ---------- READ: merkezi bulk process ---------- */
    ...multiRead('api/Emission/GetEmissionBulkProcessJobSummaries', TOPLU),
    ...multiRead('api/Emission/GetEmissionBulkProcessJobSummaryDetail', TOPLU),
    ...multiRead(
      'api/Emission/GetEmissionBulkProcessJobSummaryDetailExcel',
      TOPLU,
    ),
    ...multiRead('api/Emission/GetEmissionBulkProcessEditableRows', TOPLU),

    /* ---------- WRITE: merkezi bulk process ---------- */
    ...multiWrite('api/Emission/EmissionDataBulkProcess', TOPLU),
    ...multiWrite('api/Emission/UpdateEmissionBulkProcessRows', TOPLU),
    ...multiWrite('api/Emission/DeleteEmissionBulkProcessRows', TOPLU),

    /* ---------- READ: Business Travel Air ---------- */
    ...multiRead(
      'api/Emission/GetBusinessTravelAirImportJobSummaries',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetBusinessTravelAirImportJobSummaryDetail',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetBusinessTravelAirImportJobSummaryDetailExcel',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetBusinessTravelAirImportEditableRows',
      TOPLU,
    ),

    /* ---------- WRITE: Business Travel Air ---------- */
    ...multiWrite('api/Emission/UploadBusinessTravelAirData', TOPLU),
    ...multiWrite('api/Emission/DeleteBusinessTravelAirImportJob', TOPLU),
    ...multiWrite('api/Emission/UpdateBusinessTravelAirImportRows', TOPLU),
    ...multiWrite('api/Emission/DeleteBusinessTravelAirImportRows', TOPLU),

    /* ---------- READ: Hotel Accommodation ---------- */
    ...multiRead(
      'api/Emission/GetHotelAccommodationImportJobSummaries',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetHotelAccommodationImportJobSummaryDetail',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetHotelAccommodationImportJobSummaryDetailExcel',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetHotelAccommodationImportEditableRows',
      TOPLU,
    ),

    /* ---------- WRITE: Hotel Accommodation ---------- */
    ...multiWrite('api/Emission/UploadHotelAccommodationExcel', TOPLU, [
      'GET',
      'POST',
    ]),
    ...multiWrite('api/Emission/UpdateHotelAccommodationImportRows', TOPLU),
    ...multiWrite('api/Emission/DeleteHotelAccommodationImportRows', TOPLU),

    /* ---------- READ: Waste ---------- */
    ...multiRead('api/Emission/GetWasteEmissionImportJobSummaries', TOPLU),
    ...multiRead(
      'api/Emission/GetWasteEmissionImportJobSummaryDetail',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetWasteEmissionImportJobSummaryDetailExcel',
      TOPLU,
    ),
    ...multiRead('api/Emission/GetWasteEmissionImportEditableRows', TOPLU),

    /* ---------- WRITE: Waste ---------- */
    ...multiWrite('api/Emission/UploadWasteEmissionData', TOPLU),
    ...multiWrite('api/Emission/UpdateWasteEmissionImportRows', TOPLU),
    ...multiWrite('api/Emission/DeleteWasteEmissionImportRows', TOPLU),

    /* ---------- READ: Home Working ---------- */
    ...multiRead(
      'api/Emission/GetHomeWorkingEmissionImportJobSummaries',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetHomeWorkingEmissionImportJobSummaryDetail',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetHomeWorkingEmissionImportJobSummaryDetailExcel',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetHomeWorkingEmissionImportEditableRows',
      TOPLU,
    ),

    /* ---------- WRITE: Home Working ---------- */
    ...multiWrite('api/Emission/UploadHomeWorkingEmissionData', TOPLU),
    ...multiWrite('api/Emission/UpdateHomeWorkingEmissionImportRows', TOPLU),
    ...multiWrite('api/Emission/DeleteHomeWorkingEmissionImportRows', TOPLU),

    /* ---------- READ: Logistic ---------- */
    ...multiRead(
      'api/Emission/GetLogisticOperationImportJobSummaries',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetLogisticOperationImportJobSummaryDetail',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetLogisticOperationImportJobSummaryDetailExcel',
      TOPLU,
    ),
    ...multiRead(
      'api/Emission/GetLogisticOperationImportEditableRows',
      TOPLU,
    ),

    /* ---------- WRITE: Logistic ---------- */
    ...multiWrite('api/Emission/UploadLogisticOperationsData', TOPLU),
    ...multiWrite('api/Emission/UpdateLogisticOperationImportRows', TOPLU),
    ...multiWrite('api/Emission/DeleteLogisticOperationImportRows', TOPLU),

    /* ---------- READ/WRITE: Equipment import (admin + hesaplama) ---------- */
    ...multiRead(
      'api/Emission/GetEquipmentImportJobSummaries',
      TOPLU_OR_EKIPMAN,
    ),
    ...multiRead(
      'api/Emission/GetEquipmentImportJobSummaryDetail',
      TOPLU_OR_EKIPMAN,
    ),
    ...multiRead(
      'api/Emission/GetEquipmentImportJobSummaryDetailExcel',
      TOPLU_OR_EKIPMAN,
    ),
    ...multiRead(
      'api/Emission/GetEquipmentImportEditableRows',
      TOPLU_OR_EKIPMAN,
    ),
    ...multiWrite('api/Emission/DeleteEquipmentImportJob', TOPLU_OR_EKIPMAN),
    ...multiWrite('api/Emission/UpdateEquipmentImportRows', TOPLU_OR_EKIPMAN),
    ...multiWrite('api/Emission/DeleteEquipmentImportRows', TOPLU_OR_EKIPMAN),
    ...multiWrite('api/Company/UploadEquipmentExcel', TOPLU_OR_EKIPMAN, [
      'GET',
      'POST',
    ]),
  ];
